import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  MenuItem, Paper, Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, TextField, Tooltip } from '@mui/material';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import Edit from '@mui/icons-material/Edit';
import Save from '@mui/icons-material/Save';
import api from '../api/axios';
import { AuthContext } from '../AuthContext';
import { mandantUrl, useBusinessContext } from '../BusinessContext';
import { canReadArticles, canManageArticles } from '../components/articleAccess';
import PageHeader from '../components/PageHeader';
import LoadingIndicator from '../components/LoadingIndicator';
import LoadError from '../components/LoadError';
import { documentStatusChipSx } from '../components/documentStatusChip';

const emptyForm = () => ({ Name: '', Einheit: 'Stk', Verpackungsgroesse: '', MwstSatz: '', Standardpreis: '', Aktiv: 1 });
const status = item => Number(item.Aktiv) ? 'Freigegeben' : 'Stillgelegt';
const number = value => value == null ? '–' : Number(value).toLocaleString('de-DE', { maximumFractionDigits: 4 });
const price = value => value == null ? '–' : Number(value).toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 4 });
const columns = [['Artikelnummer', 'Artikelnummer'], ['Name', 'Bezeichnung'], ['Einheit', 'Einheit'],
  ['Verpackungsgroesse', 'Verpackungsgröße'], ['Standardpreis', 'Standardpreis'], ['MwstSatz', 'USt. %'], ['Aktiv', 'Status']];

export default function Artikel() {
  const { mandantId } = useBusinessContext();
  const auth = useContext(AuthContext);
  const read = canReadArticles(auth?.user), write = canManageArticles(auth?.user);
  const scopeState = useRef(null), requests = useRef(0);
  const [items, setItems] = useState([]), [loading, setLoading] = useState(false), [loadError, setLoadError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false), [selected, setSelected] = useState(null), [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false), [dialogError, setDialogError] = useState(''), [message, setMessage] = useState('');
  const [search, setSearch] = useState(''), [sort, setSort] = useState({ field: 'Artikelnummer', descending: false });
  const load = useCallback(async () => {
    const scope = scopeState.current;
    if (!read || !mandantId || !scope?.active || scope.mandantId !== mandantId) return false;
    const job = ++requests.current;
    setLoading(true); setLoadError('');
    try {
      const response = await api.get(mandantUrl('/artikel', mandantId));
      if (!scope.active || job !== requests.current) return false;
      setItems(response.data); return true;
    } catch (error) {
      if (scope.active && job === requests.current) setLoadError(error.response?.data?.error || 'Artikel konnten nicht geladen werden.');
      return false;
    } finally { if (scope.active && job === requests.current) setLoading(false); }
  }, [mandantId, read]);
  useEffect(() => {
    const scope = { mandantId, active: true }; scopeState.current = scope;
    setItems([]); setDialogOpen(false); setSelected(null); setSaving(false); setLoadError(''); setLoading(false); setMessage(''); setSearch('');
    load();
    return () => { scope.active = false; };
  }, [load, mandantId, write]);
  const visible = useMemo(() => items.filter(item => [item.Artikelnummer, item.Name, item.Einheit, status(item)]
    .join(' ').toLocaleLowerCase('de-DE').includes(search.toLocaleLowerCase('de-DE'))).sort((a, b) => {
    const order = sort.field === 'Name' || sort.field === 'Einheit' || sort.field === 'Artikelnummer'
      ? String(a[sort.field]).localeCompare(String(b[sort.field]), 'de', { numeric: true })
      : Number(a[sort.field] ?? -1) - Number(b[sort.field] ?? -1);
    return order * (sort.descending ? -1 : 1);
  }), [items, search, sort]);
  const openDialog = (item = null) => {
    setSelected(item);
    setForm(item ? Object.fromEntries(Object.keys(emptyForm()).map(key => [key, item[key] ?? ''])) : emptyForm());
    setDialogError(''); setDialogOpen(true);
  };
  const closeDialog = () => { setDialogOpen(false); setSelected(null); };
  const change = (key, value) => setForm(current => ({ ...current, [key]: value }));
  const save = async () => {
    if (saving || !write || !mandantId) return;
    const scope = scopeState.current;
    setSaving(true); setDialogError('');
    const data = { ...form, Aktiv: Number(form.Aktiv),
      Verpackungsgroesse: form.Verpackungsgroesse === '' ? null : String(form.Verpackungsgroesse),
      Standardpreis: form.Standardpreis === '' ? null : String(form.Standardpreis), MwstSatz: String(form.MwstSatz) };
    try {
      if (selected) await api.put(mandantUrl(`/artikel/${selected.Id}`, mandantId), data);
      else await api.post(mandantUrl('/artikel', mandantId), data);
      if (!scope.active) return;
      closeDialog();
      if (await load()) setMessage('Artikel erfolgreich gespeichert.');
    } catch (error) {
      if (scope.active) setDialogError(error.response?.data?.error || 'Der Artikel konnte nicht gespeichert werden.');
    } finally { if (scope.active) setSaving(false); }
  };
  if (!read) return <Alert severity="warning">Keine Berechtigung für Artikel.</Alert>;
  return <Box>
    <PageHeader title="Artikel" action={write && <Button variant="contained" size="small" startIcon={<Add />} disabled={!mandantId} onClick={() => openDialog()}>Hinzufügen</Button>} />
    {!mandantId && <Alert severity="info">Bitte einen Mandanten auswählen.</Alert>}
    {loading && <LoadingIndicator label="Artikel werden geladen …" />}
    {loadError && <LoadError onRetry={load}>{loadError}</LoadError>}
    <TextField size="small" label="Artikel suchen" value={search} onChange={event => setSearch(event.target.value)} sx={{ mb: 2 }} />
    <TableContainer component={Paper}><Table size="small" aria-label="Artikel"><TableHead><TableRow>
      {columns.map(([field, label]) => <TableCell key={field}><TableSortLabel active={sort.field === field} direction={sort.descending ? 'desc' : 'asc'} onClick={() => setSort({ field, descending: sort.field === field ? !sort.descending : false })}>{label}</TableSortLabel></TableCell>)}
      <TableCell align="right">Aktion</TableCell>
    </TableRow></TableHead><TableBody>
      {!loading && !loadError && mandantId && !visible.length && <TableRow><TableCell colSpan={8} align="center">Keine Artikel.</TableCell></TableRow>}
      {visible.map(item => <TableRow key={item.Id} hover>
        <TableCell>{item.Artikelnummer}</TableCell><TableCell>{item.Name}</TableCell><TableCell>{item.Einheit}</TableCell>
        <TableCell>{number(item.Verpackungsgroesse)}</TableCell><TableCell>{price(item.Standardpreis)}</TableCell><TableCell>{number(item.MwstSatz)}</TableCell>
        <TableCell><Chip size="small" sx={documentStatusChipSx(Number(item.Aktiv) ? 'FREIGEGEBEN' : 'STILLGELEGT')} label={status(item)} /></TableCell>
        <TableCell align="right">{write && <Tooltip title="Bearbeiten"><IconButton size="small" color="primary" aria-label={`Artikel ${item.Artikelnummer} bearbeiten`} onClick={() => openDialog(item)}><Edit fontSize="small" /></IconButton></Tooltip>}</TableCell>
      </TableRow>)}
    </TableBody></Table></TableContainer>
    <Dialog open={write && dialogOpen} onClose={saving ? undefined : closeDialog} maxWidth="sm" fullWidth>
      <DialogTitle>{selected ? 'Artikel bearbeiten' : 'Artikel hinzufügen'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
        {dialogError && <Alert severity="error">{dialogError}</Alert>}
        <TextField size="small" label="Artikelnummer" value={selected?.Artikelnummer ?? ''} placeholder="Automatisch" disabled />
        <TextField size="small" label="Bezeichnung" value={form.Name} onChange={event => change('Name', event.target.value)} required />
        <TextField size="small" label="Einheit" value={form.Einheit} onChange={event => change('Einheit', event.target.value)} required />
        <TextField size="small" label="Verpackungsgröße" value={form.Verpackungsgroesse} onChange={event => change('Verpackungsgroesse', event.target.value)} slotProps={{ htmlInput: { inputMode: 'decimal' } }} />
        <TextField size="small" label="Standardpreis EUR" value={form.Standardpreis} onChange={event => change('Standardpreis', event.target.value)} slotProps={{ htmlInput: { inputMode: 'decimal' } }} />
        <TextField size="small" label="USt. %" value={form.MwstSatz} onChange={event => change('MwstSatz', event.target.value)} slotProps={{ htmlInput: { inputMode: 'decimal' } }} required />
        <TextField size="small" select label="Status" value={form.Aktiv} onChange={event => change('Aktiv', event.target.value)}><MenuItem value={1}>Freigegeben</MenuItem><MenuItem value={0}>Stillgelegt</MenuItem></TextField>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" startIcon={<Close />} onClick={closeDialog} disabled={saving}>Abbrechen</Button>
        <Button variant="contained" startIcon={<Save />} onClick={save} disabled={saving || !form.Name.trim() || !form.Einheit.trim() || String(form.MwstSatz).trim() === ''}>Speichern</Button>
      </DialogActions>
    </Dialog>
    <Snackbar open={Boolean(message)} autoHideDuration={4000} onClose={() => setMessage('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}><Alert severity="success" variant="filled">{message}</Alert></Snackbar>
  </Box>;
}
